import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Row, Col, Breadcrumb, Card, Tag, Typography, Empty, Spin,
    Space, Progress, Alert, Button, message
} from "antd";
import {
    HomeOutlined, ExperimentOutlined, ArrowRightOutlined, ClockCircleOutlined,
    SafetyCertificateOutlined, ReloadOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;

const PRODUCTION_STATUS_COLORS = {
    "Pending": "orange",
    "Ongoing": "blue",
    "Finished": "green",
};

const formatKg = (value) =>
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const productLabel = (batch) => {
    if (!batch) return "-";
    if (batch.sp_code && batch.sp_name) return `${batch.sp_code} - ${batch.sp_name}`;
    return batch.sp_name || batch.sp_code || "-";
};

const ProductionProcess = () => {
    const navigate = useNavigate();
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const fetchBatches = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await api.post("/production/ongoing_batches.php", {});
            if (res.data.status !== "success") {
                throw new Error(res.data.message || "Failed to load ongoing production batches.");
            }
            setBatches(res.data.dataset || []);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "Failed to load ongoing production batches.";
            setBatches([]);
            setLoadError(msg);
            message.error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBatches();
    }, [fetchBatches]);

    const producedPercent = (batch) => {
        const targetKg = parseFloat(batch.target_kg || 0);
        const actualKg = parseFloat(batch.actual_kg || 0);
        if (targetKg <= 0) return 0;
        return Math.min(100, Math.round((actualKg / targetKg) * 100));
    };

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Production' },
                                { title: 'Production Process' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <ExperimentOutlined /> Production Process
                        </h1>
                        <p className="fluid-page-subtitle">
                            Select an ongoing production batch to record inputs, approvals and quality control
                        </p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <Space>
                            <Typography.Text type="secondary">
                                {batches.length} ongoing batch{batches.length === 1 ? "" : "es"}
                            </Typography.Text>
                        </Space>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-card-header">
                        <h3 className="fluid-card-title">Ongoing Production Batches</h3>
                    </div>
                    <Spin spinning={loading}>
                        {!loading && loadError ? (
                            <Alert
                                type="error"
                                showIcon
                                message="Could not load production batches"
                                description={loadError}
                                action={<Button size="small" icon={<ReloadOutlined />} onClick={fetchBatches}>Retry</Button>}
                                style={{ margin: "0 0 16px" }}
                            />
                        ) : null}
                        {!loading && !loadError && batches.length === 0 ? (
                            <Empty
                                description="No ongoing production batches found"
                                style={{ padding: "40px 0" }}
                            />
                        ) : !loadError ? (
                            <Row gutter={[16, 16]}>
                                {batches.map((b) => (
                                    <Col xs={24} sm={12} lg={8} xl={6} key={b.prodb_id}>
                                        <Card
                                            hoverable
                                            className="fluid-collection-card"
                                            onClick={() => navigate(`/production/production-process/${b.prodb_id}`)}
                                            style={{ height: "100%" }}
                                        >
                                            <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }}>
                                                <Typography.Text strong style={{ fontSize: 15 }}>
                                                    {b.prodb_ref}
                                                </Typography.Text>
                                                <Tag color={PRODUCTION_STATUS_COLORS[b.batch_status] || "default"} style={{ fontWeight: 600 }}>
                                                    {b.batch_status}
                                                </Tag>
                                            </Space>
                                            <Typography.Text type="secondary" style={{ display: "block" }}>
                                                {productLabel(b)}
                                            </Typography.Text>
                                            <Typography.Text style={{ display: "block", marginTop: 4 }}>
                                                Processed By: <Tag>{b.processed_by_name || "-"}</Tag>
                                            </Typography.Text>
                                            <div style={{ marginTop: 10 }}>
                                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                    Target: {Number(b.target_qty || 0).toLocaleString()} qty /{" "}
                                                    {formatKg(b.target_kg)} kg
                                                </Typography.Text>
                                            </div>
                                            <div>
                                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                    Actual: {Number(b.actual_qty || 0).toLocaleString()} qty /{" "}
                                                    {formatKg(b.actual_kg)} kg
                                                </Typography.Text>
                                            </div>
                                            <Progress
                                                percent={producedPercent(b)}
                                                size="small"
                                                strokeColor="#1677ff"
                                                style={{ marginTop: 8 }}
                                            />
                                            {(Number(b.pending_approval_count || 0) > 0 || Number(b.pending_qc_count || 0) > 0) && (
                                                <Space size={4} style={{ marginTop: 4, flexWrap: "wrap" }}>
                                                    {Number(b.pending_approval_count || 0) > 0 && (
                                                        <Tag color="orange" icon={<ClockCircleOutlined />}>
                                                            {Number(b.pending_approval_count)} pending approval
                                                        </Tag>
                                                    )}
                                                    {Number(b.pending_qc_count || 0) > 0 && (
                                                        <Tag color="gold" icon={<SafetyCertificateOutlined />}>
                                                            {Number(b.pending_qc_count)} pending QC
                                                        </Tag>
                                                    )}
                                                </Space>
                                            )}
                                            <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                    {b.prod_start_date ? dayjs(b.prod_start_date).format("YYYY-MM-DD") : "-"}
                                                    {" \u2192 "}
                                                    {b.prod_end_date ? dayjs(b.prod_end_date).format("YYYY-MM-DD") : "-"}
                                                </Typography.Text>
                                                <Typography.Text style={{ color: "#1677ff" }}>
                                                    Open <ArrowRightOutlined />
                                                </Typography.Text>
                                            </div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        ) : null}
                    </Spin>
                </div>
            </Content>

            <Footerdiv />
        </Layout>
    );
};

export default ProductionProcess;
